import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  createCustomer,
  getCustomerTimeline,
  getCustomers,
  updateCustomer,
} from "../api/customers";
import { getApiError } from "../api/errors";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import useDebouncedValue from "../hooks/useDebouncedValue";
import PageHeader from "../components/PageHeader";
import MainLayout from "../layouts/MainLayout";
import {
  createIdempotencyKey,
  formatDate,
  titleCase,
} from "../utils/format";

const PAGE_SIZE = 24;
const emptyForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  external_id: "",
  notes: "",
  status: "active",
};

function cleanOptional(value) {
  const normalized = String(value || "").trim();
  return normalized || null;
}

export default function Customers() {
  const { canEdit, selectedWorkspace, selectedStore } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const debouncedQuery = useDebouncedValue(query.trim(), 350);

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError("");

      try {
        const response = await getCustomers({
          query: debouncedQuery || undefined,
          status: statusFilter || undefined,
          page,
          page_size: PAGE_SIZE,
          _refresh: force ? Date.now() : undefined,
        });
        setCustomers(response.data?.items || []);
        setTotal(Number(response.data?.total || 0));
      } catch (requestError) {
        setError(getApiError(requestError, "Could not load customers."));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [debouncedQuery, page, statusFilter],
  );

  useEffect(() => {
    load();
  }, [load, selectedStore?.public_id, selectedWorkspace?.public_id]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusFilter, selectedStore?.public_id, selectedWorkspace?.public_id]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activeTotal = useMemo(
    () => customers.filter((item) => item.status === "active").length,
    [customers],
  );

  const openCreate = () => {
    setEditingCustomer(null);
    setForm(emptyForm);
    setIdempotencyKey(createIdempotencyKey("customer"));
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      external_id: customer.external_id || "",
      notes: customer.notes || "",
      status: customer.status || "active",
    });
    setError("");
    setDialogOpen(true);
  };

  const openHistory = async (customer) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistory(null);
    setError("");
    try {
      const response = await getCustomerTimeline(customer.public_id);
      setHistory(response.data);
    } catch (requestError) {
      setHistoryOpen(false);
      setError(getApiError(requestError, "Could not load customer history."));
    } finally {
      setHistoryLoading(false);
    }
  };

  const save = async () => {
    if (!form.first_name.trim()) {
      setError("First name is required.");
      return;
    }

    const payload = {
      first_name: form.first_name.trim(),
      last_name: cleanOptional(form.last_name),
      phone: cleanOptional(form.phone),
      email: cleanOptional(form.email),
      external_id: cleanOptional(form.external_id),
      notes: cleanOptional(form.notes),
    };

    setSaving(true);
    setError("");
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.public_id, {
          ...payload,
          status: form.status,
        });
        setSuccess("Customer updated.");
      } else {
        await createCustomer(payload, idempotencyKey);
        setSuccess("Customer created.");
      }
      setDialogOpen(false);
      await load({ force: true });
    } catch (requestError) {
      setError(getApiError(requestError, "Could not save the customer."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <PageHeader
        title="Customers"
        description="Workspace CRM records used by quotations and the AI employee."
        actions={(
          <>
            <Chip label={`${total.toLocaleString()} customers`} sx={{ fontWeight: 800 }} />
            <Chip
              label={`${activeTotal} active on this page`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Button
              variant="outlined"
              disabled={refreshing}
              onClick={() => load({ force: true })}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            {canEdit && (
              <Button variant="contained" onClick={openCreate}>
                Add customer
              </Button>
            )}
          </>
        )}
      />

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          You have read-only CRM access.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Flexible customer search"
          placeholder="Name, phone, email, external ID, or notes"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ maxWidth: 720, bgcolor: "white" }}
        />
        <FormControl sx={{ minWidth: 190, bgcolor: "white" }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <LoadingPanel label="Loading customer records…" />
      ) : customers.length === 0 ? (
        <EmptyPanel
          icon="👥"
          title={debouncedQuery ? "No matching customers" : "No customers yet"}
          description={
            debouncedQuery
              ? "Try a shorter name, phone, email, or external reference."
              : "Add customers manually or let the Customer Agent prepare one with confirmation."
          }
          action={
            canEdit && !debouncedQuery ? (
              <Button variant="contained" onClick={openCreate}>
                Add first customer
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <Grid container spacing={2.5}>
            {customers.map((customer) => (
              <Grid item xs={12} sm={6} lg={4} key={customer.public_id}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 14px 35px rgba(15,23,42,.05)",
                    opacity: customer.status === "inactive" ? 0.75 : 1,
                  }}
                >
                  <CardContent sx={{ p: 2.75 }}>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={900} noWrap>
                          {customer.display_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {customer.email || customer.phone || "No contact details"}
                        </Typography>
                      </Box>
                      <Chip
                        label={titleCase(customer.status)}
                        color={customer.status === "active" ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: 800 }}
                      />
                    </Stack>

                    <Stack spacing={1.1} sx={{ mt: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography fontWeight={750}>{customer.phone || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          External ID
                        </Typography>
                        <Typography fontWeight={750}>{customer.external_id || "—"}</Typography>
                      </Box>
                      {customer.notes && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Notes
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.25, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                          >
                            {customer.notes}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                      Created {formatDate(customer.created_at)} · {titleCase(customer.source)}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="outlined"
                        onClick={() => openHistory(customer)}
                        sx={{ textTransform: "none", fontWeight: 800 }}
                      >
                        View history
                      </Button>
                      {canEdit && (
                        <Button
                          variant="outlined"
                          onClick={() => openEdit(customer)}
                          sx={{ textTransform: "none", fontWeight: 800 }}
                        >
                          Edit customer
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {pageCount > 1 && (
            <Stack alignItems="center" spacing={1} sx={{ mt: 4 }}>
              <Pagination
                page={page}
                count={pageCount}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
              <Typography variant="caption" color="text.secondary">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
              </Typography>
            </Stack>
          )}
        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle fontWeight={900}>
          {editingCustomer ? "Edit customer" : "Add customer"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First name"
                value={form.first_name}
                onChange={(event) => setForm((current) => ({ ...current, first_name: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last name"
                value={form.last_name}
                onChange={(event) => setForm((current) => ({ ...current, last_name: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={editingCustomer ? 6 : 12}>
              <TextField
                fullWidth
                label="External customer ID"
                value={form.external_id}
                onChange={(event) => setForm((current) => ({ ...current, external_id: event.target.value }))}
              />
            </Grid>
            {editingCustomer && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Notes"
                value={form.notes}
                onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : editingCustomer ? "Save changes" : "Create customer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle fontWeight={900}>Customer history</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <LoadingPanel label="Loading customer history…" />
          ) : !history ? null : (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  {history.customer.display_name}
                </Typography>
                <Typography color="text.secondary">
                  {history.customer.phone || history.customer.email || "No contact details"}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  {(history.identities || []).map((identity) => (
                    <Chip
                      key={identity.public_id}
                      label={`${titleCase(identity.provider)} · ${identity.profile_name || identity.phone_normalized || "Identity"}`}
                      color={identity.provider === "whatsapp" ? "success" : "secondary"}
                      variant="outlined"
                    />
                  ))}
                  {(history.identities || []).length === 0 && (
                    <Chip label="No channel identities yet" variant="outlined" />
                  )}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography fontWeight={900} sx={{ mb: 1 }}>Recent messages</Typography>
                {(history.recent_messages || []).length === 0 ? (
                  <Typography color="text.secondary">No conversation history yet.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {history.recent_messages.map((message) => (
                      <Box
                        key={message.public_id}
                        sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}
                      >
                        <Stack direction="row" justifyContent="space-between" gap={2}>
                          <Typography variant="caption" fontWeight={900}>
                            {message.direction === "inbound" ? "Customer" : titleCase(message.sender_type)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(message.occurred_at)}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                          {message.text || `[${titleCase(message.content_type)} message]`}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography fontWeight={900} sx={{ mb: 1 }}>Recent quotations</Typography>
                {(history.quotations || []).length === 0 ? (
                  <Typography color="text.secondary">No quotations for this customer.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {history.quotations.map((quotation) => (
                      <Stack
                        key={quotation.public_id}
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        gap={1}
                        sx={{ p: 1.5, borderRadius: 2.5, border: "1px solid #e2e8f0" }}
                      >
                        <Box>
                          <Typography fontWeight={850}>{quotation.quotation_number}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(quotation.created_at)}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={titleCase(quotation.status)} size="small" />
                          <Typography fontWeight={900}>
                            {quotation.currency} {quotation.total}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
